import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const SCOPES_URL = `${getBaseUrl()}conductor/oauth-scopes`;
const ENDPOINTS_DELETE_URL = `${getBaseUrl()}conductor/oauth-scope-endpoints`;

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', '*'];

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseJsonSafely = async (res) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const OAuthScopeEndpointsDrawer = ({ open, onClose, scope }) => {
  const { canAdd, canDelete } = useFunctionPermissions(FC.EXTERNAL_INTEGRATION);
  const scopeName = scope?.scope_name || '';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEndpointId, setSelectedEndpointId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [endpointForm, setEndpointForm] = useState({
    pathPattern: '',
    httpMethod: 'GET',
  });

  const loadEndpoints = async () => {
    if (!scopeName) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${SCOPES_URL}/${encodeURIComponent(scopeName)}/endpoints`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error('Failed to load endpoints');
      const data = await parseJsonSafely(res);
      const list = Array.isArray(data) ? data : [];
      setRows(
        list.map((ep) => ({
          id: ep.id,
          http_method: ep.http_method,
          path_pattern: ep.path_pattern,
        }))
      );
    } catch (e) {
      toast.error(e.message || 'Failed to load endpoints');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && scopeName) {
      setSelectedEndpointId(null);
      loadEndpoints();
    }
  }, [open, scopeName]);

  const columns = useMemo(
    () => [
      { field: 'http_method', headerName: 'Method', width: 110 },
      { field: 'path_pattern', headerName: 'Path Pattern', flex: 1, minWidth: 220 },
    ],
    []
  );

  const saveEndpoint = async () => {
    if (!canAdd) return;
    try {
      if (!endpointForm.pathPattern.trim()) {
        toast.error('Path pattern is required (e.g. /conductor/**)');
        return;
      }
      setSubmitting(true);
      const res = await fetch(
        `${SCOPES_URL}/${encodeURIComponent(scopeName)}/endpoints`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            pathPattern: endpointForm.pathPattern.trim(),
            httpMethod: endpointForm.httpMethod,
          }),
        }
      );
      const body = await parseJsonSafely(res);
      if (!res.ok) throw new Error(body?.error || 'Failed to add endpoint');
      toast.success('Endpoint added');
      setAddOpen(false);
      setEndpointForm({ pathPattern: '', httpMethod: 'GET' });
      await loadEndpoints();
    } catch (e) {
      toast.error(e.message || 'Failed to add endpoint');
    } finally {
      setSubmitting(false);
    }
  };

  const openDelete = () => {
    if (!canDelete || !selectedEndpointId) return;
    setDeleteOpen(true);
  };

  const handleDeleteEndpoint = async () => {
    if (!canDelete || !selectedEndpointId) return;
    try {
      setDeleteLoading(true);
      const res = await fetch(`${ENDPOINTS_DELETE_URL}/${selectedEndpointId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const body = await parseJsonSafely(res);
      if (!res.ok) throw new Error(body?.error || 'Failed to delete endpoint');
      setDeleteOpen(false);
      toast.success('Endpoint removed');
      setSelectedEndpointId(null);
      await loadEndpoints();
    } catch (e) {
      toast.error(e.message || 'Failed to delete endpoint');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!scope) return null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: ht.drawerBg }}>
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0, mb: 0 }}>
        <Box sx={{ pr: 2, minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
            Scope Endpoints
          </Typography>
          <Typography variant="body2" sx={{ color: ht.secondaryTab, fontFamily: 'monospace' }}>
            {scope.display_name || scopeName} ({scopeName})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={canAdd ? 'Add endpoint' : 'No add permission'}>
            <span>
            <IconButton size="small" onClick={() => canAdd && setAddOpen(true)} disabled={!canAdd} sx={harmonateDialogCloseIconSx}>
              <AddIcon fontSize="small" />
            </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'No delete permission'}>
            <span>
              <IconButton
                size="small"
                disabled={!selectedEndpointId || !canDelete}
                onClick={openDelete}
                sx={harmonateDialogCloseIconSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose} aria-label="Close drawer" sx={harmonateDialogCloseIconSx}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, p: 2, minHeight: 0 }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          onRowClick={(params) => setSelectedEndpointId(params.row.id)}
          rowSelectionModel={selectedEndpointId ? [selectedEndpointId] : []}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{
            ...harmonateDataGridSx,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs" PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add endpoint</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            label="Path pattern"
            size="small"
            placeholder="/conductor/**"
            value={endpointForm.pathPattern}
            onChange={(e) => setEndpointForm((p) => ({ ...p, pathPattern: e.target.value }))}
            fullWidth
            required
          />
          <Autocomplete
            size="small"
            options={HTTP_METHODS}
            value={endpointForm.httpMethod}
            onChange={(_, value) =>
              setEndpointForm((p) => ({ ...p, httpMethod: value || 'GET' }))
            }
            renderInput={(params) => <TextField {...params} label="HTTP method" />}
            sx={harmonateFormFieldsSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" disabled={submitting || !canAdd} onClick={saveEndpoint} sx={harmonateProceedButtonSx}>
            {submitting ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteLoading && setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>Are you sure you want to remove this endpoint rule?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteEndpoint}
            disabled={deleteLoading || !canDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OAuthScopeEndpointsDrawer;
